import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { createAutoClient, updateAutoClient } from '../../utils/api';

const inputCls =
  'w-full bg-black border border-divider rounded-lg px-3 py-2 text-white text-sm focus:border-lime focus:outline-none';

export const ClientModal = ({ client, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: client?.name || '',
    trade: client?.trade || 'HVAC',
    services: (client?.services || []).join(', '),
    service_area: client?.service_area || '',
    brand_voice: client?.brand_voice || '',
    promotions: client?.promotions || '',
    facebook_page_id: client?.platform_accounts?.facebook_page_id || '',
    instagram_account_id: client?.platform_accounts?.instagram_account_id || '',
    meta_ad_account_id: client?.platform_accounts?.meta_ad_account_id || '',
    google_ads_customer_id: client?.platform_accounts?.google_ads_customer_id || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      trade: form.trade,
      services: form.services.split(',').map((s) => s.trim()).filter(Boolean),
      service_area: form.service_area,
      brand_voice: form.brand_voice,
      promotions: form.promotions,
      platform_accounts: {
        facebook_page_id: form.facebook_page_id,
        instagram_account_id: form.instagram_account_id,
        meta_ad_account_id: form.meta_ad_account_id,
        google_ads_customer_id: form.google_ads_customer_id,
      },
    };
    try {
      const res = client
        ? await updateAutoClient(client.id, payload)
        : await createAutoClient(payload);
      toast.success(client ? 'Client updated' : 'Client added');
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="client-modal">
      <div className="bg-card-bg border border-divider rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-divider">
          <h3 className="text-white font-bold text-lg">{client ? 'Edit Client' : 'New HVAC Client'}</h3>
          <button onClick={onClose} data-testid="client-modal-close" className="text-gray hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Business Name *</label>
            <input data-testid="client-name-input" className={inputCls} value={form.name} onChange={set('name')} placeholder="Cool Breeze HVAC" />
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Trade *</label>
            <select data-testid="client-trade-select" className={inputCls} value={form.trade} onChange={set('trade')}>
              <option value="HVAC">HVAC</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Electrical">Electrical</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Services (comma separated)</label>
            <input data-testid="client-services-input" className={inputCls} value={form.services} onChange={set('services')} placeholder="AC repair, furnace install, duct cleaning" />
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Service Area</label>
            <input data-testid="client-area-input" className={inputCls} value={form.service_area} onChange={set('service_area')} placeholder="Austin, TX metro" />
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Brand Voice / Notes</label>
            <textarea data-testid="client-voice-input" className={inputCls} rows={2} value={form.brand_voice} onChange={set('brand_voice')} placeholder="Friendly, trustworthy, family-owned" />
          </div>
          <div>
            <label className="text-xs text-gray uppercase font-semibold">Current Promotions</label>
            <textarea data-testid="client-promos-input" className={inputCls} rows={2} value={form.promotions} onChange={set('promotions')} placeholder="$79 AC tune-up special" />
          </div>
          <div className="border-t border-divider pt-4">
            <p className="text-xs text-gray uppercase font-semibold mb-3">Connected Platform Accounts (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="client-fb-input" className={inputCls} value={form.facebook_page_id} onChange={set('facebook_page_id')} placeholder="Facebook Page ID" />
              <input data-testid="client-ig-input" className={inputCls} value={form.instagram_account_id} onChange={set('instagram_account_id')} placeholder="Instagram Account ID" />
              <input data-testid="client-meta-ads-input" className={inputCls} value={form.meta_ad_account_id} onChange={set('meta_ad_account_id')} placeholder="Meta Ad Account ID" />
              <input data-testid="client-google-ads-input" className={inputCls} value={form.google_ads_customer_id} onChange={set('google_ads_customer_id')} placeholder="Google Ads Customer ID" />
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-divider flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-gray-light hover:text-white text-sm">Cancel</button>
          <button
            data-testid="client-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Client'}
          </button>
        </div>
      </div>
    </div>
  );
};
